/**
 * The reticle and the parry timing ring.
 *
 * The parry window is a fact about *time* — valid from `PARRY_WINDOW_MS` before
 * a shot lands until the impact hold runs out — and until now the only thing
 * that expressed it was a line of text at the bottom of the screen, where a
 * player watching an incoming bolt never looks. This draws it where the eyes
 * already are.
 *
 * A ring contracts toward the reticle in step with the shot, and the fixed
 * target ring marks the moment the window opens. Outside the target, acting is
 * too early and the server will find no candidate; on or inside it, the parry
 * counts. It is the approach-circle read from rhythm games, and it is honest —
 * the geometry is derived from the published timings, not tuned by eye.
 *
 * Lives in its own component because the tutorial needs the identical read: it
 * is where the timing is taught, and teaching it against a different indicator
 * than the match uses would be worse than not teaching it at all.
 */
import { useEffect, useRef } from 'react';
import { TIMING } from '@spell/shared-types';
import { scene as bus } from '../scene/sceneBus';
import { castState } from '../gameplay/castState';
import { micLevels } from '../voice/mic';
import { sfx } from '../audio/sfx';

/** How long before impact the ring appears. Longer than the window itself, so
 *  the contraction can be read as a rhythm rather than appearing already open. */
const APPROACH_S = 1.3;
/** Ring scale at first appearance, and at the moment of impact. */
const SCALE_FAR = 3.0;
const SCALE_IMPACT = 0.6;

const WINDOW_S = TIMING.PARRY_WINDOW_MS / 1000;

/** Scale of the ring at the instant the window opens — where the target sits. */
export const WINDOW_SCALE =
  SCALE_IMPACT + (SCALE_FAR - SCALE_IMPACT) * (WINDOW_S / APPROACH_S);

const scaleFor = (seconds: number) =>
  SCALE_IMPACT + (SCALE_FAR - SCALE_IMPACT) * Math.min(1, Math.max(0, seconds) / APPROACH_S);

type Stage = 'idle' | 'approach' | 'open' | 'last';

export function ParryTiming() {
  const root = useRef<HTMLDivElement>(null);
  const ring = useRef<HTMLDivElement>(null);
  const target = useRef<HTMLDivElement>(null);
  const danger = useRef<HTMLDivElement>(null);
  const voice = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let raf = 0;
    // Cue once per shot, not once per frame, and only when a stage is actually
    // entered — a shot that is already inside the window when it first becomes
    // visible must still announce itself.
    let cuedOpenFor = '';
    let cuedLastFor = '';
    let stage: Stage = 'idle';

    // Both rings fill the reticle box, so `scale` alone centres them — the same
    // convention `.danger-ring` already uses.
    if (target.current) {
      target.current.style.transform = `scale(${WINDOW_SCALE.toFixed(3)})`;
    }

    const loop = () => {
      raf = requestAnimationFrame(loop);

      // The nearest parryable shot aimed at us. An unparryable one gets no
      // ring: promising a window that cannot be met is the failure this is
      // meant to remove, not one to reproduce in a new place.
      let nearest: (typeof bus.projectiles)[number] | null = null;
      for (const p of bus.projectiles) {
        if (!p.incoming || !p.parryable) continue;
        if (p.resolveIn > 0) {
          // A shot already holding always wins: it is the most urgent thing
          // on screen and it is about to stop being actionable.
          if (!nearest || nearest.resolveIn <= 0 || p.resolveIn < nearest.resolveIn) nearest = p;
          continue;
        }
        if (nearest && nearest.resolveIn > 0) continue;
        if (!nearest || p.eta < nearest.eta) nearest = p;
      }

      const holding = Boolean(nearest && nearest.resolveIn > 0);
      const seconds = nearest ? (holding ? 0 : nearest.eta) : Number.POSITIVE_INFINITY;
      const visible = Boolean(nearest) && (holding || seconds <= APPROACH_S);

      const next: Stage = !visible ? 'idle' : holding ? 'last' : seconds <= WINDOW_S ? 'open' : 'approach';

      if (next !== stage) {
        stage = next;
        if (nearest && next === 'open' && cuedOpenFor !== nearest.id) {
          cuedOpenFor = nearest.id;
          sfx.parryWindow();
        }
        if (nearest && next === 'last' && cuedLastFor !== nearest.id) {
          cuedLastFor = nearest.id;
          // A shot fast enough to arrive without ever rendering an open frame
          // still owes the player the window cue before the last-chance one.
          if (cuedOpenFor !== nearest.id) {
            cuedOpenFor = nearest.id;
            sfx.parryWindow();
          }
          sfx.parryLastChance();
        }
      }

      const el = ring.current;
      if (el) {
        el.style.opacity = visible ? '1' : '0';
        if (visible) {
          // Once the shot is holding there is no time left to count down, so
          // the ring stops contracting and beats in place instead. A ring
          // frozen at its smallest is the least visible thing on screen at
          // exactly the moment it matters most, and the shot is filling the
          // frame behind it.
          const scale = holding
            ? SCALE_IMPACT * (1.55 + Math.sin(performance.now() / 45) * 0.3)
            : scaleFor(seconds);
          el.style.transform = `scale(${scale.toFixed(3)})`;
        }
        el.dataset.stage = next;
      }
      if (root.current) root.current.dataset.stage = next;
      if (target.current) target.current.style.opacity = visible ? '1' : '0';

      // The soft red bloom stays, but it is a different statement — "something
      // is coming" rather than "act now" — so it is damped while the crisp ring
      // is carrying the timing.
      if (danger.current) {
        const d = Math.min(1, Math.max(0, bus.danger));
        danger.current.style.opacity = (d * (visible ? 0.45 : 1)).toFixed(3);
        danger.current.style.transform = `scale(${(1.5 - d * 0.5).toFixed(3)})`;
      }

      // Microphone liveness, breathing with the input level. It shares a centre
      // with the timing rings but is deliberately the smallest thing here —
      // inside the reticle rather than outside it, and arcane rather than red —
      // because it is a statement about the player's own voice, not about a
      // shot, and the two must never be confused at a glance.
      if (voice.current) {
        const el = voice.current;
        const level = Math.min(1, micLevels.level * 7);
        const state = !castState.active
          ? micLevels.speaking
            ? 'ambient'
            : 'idle'
          : castState.transcript
            ? 'heard'
            : 'listening';
        if (el.dataset.state !== state) el.dataset.state = state;
        el.style.transform = `scale(${(1 + level * 0.5).toFixed(3)})`;
      }
    };

    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, []);

  return (
    <div className="hud__reticle" ref={root} data-stage="idle" data-testid="parry-timing">
      <div className="reticle-ring" />
      <div className="mic-ring" ref={voice} data-state="idle" data-testid="mic-ring" />
      <div className="reticle-dot" />
      <div className="danger-ring" ref={danger} />
      <div className="parry-timing__target" ref={target} />
      <div className="parry-timing__ring" ref={ring} data-stage="idle" data-testid="parry-timing-ring" />
    </div>
  );
}
